import { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
  ref, onValue, query, orderByChild, equalTo,
  push, set, update, remove, get,
} from 'firebase/database';
import { db } from '../lib/firebase.js';
import { useAuth } from '../lib/useAuth.jsx';
import { useAdminAccess } from '../lib/useAdminAccess.jsx';
import {
  generateLoginCode,
  claimLoginCode,
  releaseLoginCode,
} from '../lib/loginCode.js';
import { LEVELS, GAMES } from '../data/gamesCatalog.js';
import { engagementSummary, goalActivitySummary } from '../lib/sessions.js';
import './AdminResidents.css';

// -----------------------------------------------------------------
// Usage-stats helpers
//
// The summary column and the expandable detail both read from the
// resident's account.sessions map (already loaded by the roster
// query — no extra Firebase reads needed). The 30-day numbers reuse
// the SAME functions the resident dashboard uses (engagementSummary,
// goalActivitySummary from sessions.js) so staff and resident never
// see contradictory figures.
//
// The one thing sessions.js does NOT provide is a per-GAME rollup —
// it rolls up by goal. We compute that locally here, all-time, for
// the "most played" list with last-played dates.
// -----------------------------------------------------------------

// All-time per-game rollup. Returns games sorted most-played first,
// each with play count and the most recent started_at.
function gameRollup(sessionsMap) {
  if (!sessionsMap) return [];
  const byGame = new Map();
  for (const s of Object.values(sessionsMap)) {
    if (!s || !s.started_at) continue;
    const entry = byGame.get(s.game_id) || { plays: 0, lastPlayed: 0 };
    entry.plays += 1;
    if (s.started_at > entry.lastPlayed) entry.lastPlayed = s.started_at;
    byGame.set(s.game_id, entry);
  }
  return [...byGame.entries()]
    .map(([gameId, e]) => ({
      gameId,
      name: GAMES[gameId]?.name || gameId,
      plays: e.plays,
      lastPlayed: e.lastPlayed,
    }))
    .sort((a, b) => b.plays - a.plays || b.lastPlayed - a.lastPlayed);
}

// AdminResidents — community-scoped resident roster.
// Visible to both the super-admin (for any community) and the
// community admin (only their own communities).
//
// Capabilities (in order added):
//   - List active residents, with assessment status and last-active
//   - Add new residents (auto-generated sign-in code)
//   - Re-take assessment on behalf of a resident
//   - Archive / restore residents
//   - Pair / unpair a Hexnode-managed device with a resident, using
//     the short pairing code shown on the tablet's welcome screen
//
// The device pairing flow:
//   1. Admin opens a tablet whose Hexnode policy points at
//      /welcome.html?did=%udid%
//   2. The Welcome page sees an unknown UDID, generates a short
//      pairing code (stored at /brain_health/device_pairing/{code}
//      with an expiration), and displays it on the tablet screen.
//   3. Admin returns to the resident roster on their own computer,
//      clicks "Pair device" on the resident's row, types the short
//      code, clicks Save.
//   4. We look up device_pairing/{code} to get the UDID, write the
//      UDID to the resident's device_udid field AND to
//      device_index/{udid} → {accountId, communityCode}, then
//      delete the pairing record.
//   5. The tablet, on its next refresh, sees the now-paired UDID
//      and signs the resident in automatically.
export default function AdminResidents() {
  const { communityCode: rawCommunityCode } = useParams();
  const communityCode = decodeURIComponent(rawCommunityCode);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isSuperAdmin, communities: adminCommunities } = useAdminAccess();

  const lastAssessment = location.state?.lastAssessment || null;

  const [community, setCommunity] = useState(null);
  const [residents, setResidents] = useState(null);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [residentName, setResidentName] = useState('');
  const [residentNotes, setResidentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);

  // Which resident row's usage-stats detail panel is open (null = none).
  const [expandedResidentId, setExpandedResidentId] = useState(null);

  // Device pairing state — which row's "Pair device" form is open,
  // what they've typed, whether a pair/unpair operation is in flight.
  const [pairingResidentId, setPairingResidentId] = useState(null);
  const [pairingCode, setPairingCode] = useState('');
  const [pairingSubmitting, setPairingSubmitting] = useState(false);
  const [pairingError, setPairingError] = useState(null);

  // Admin-management state (super-admin only). Adding/removing the
  // co-admins who can manage this community.
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminSubmitting, setAdminSubmitting] = useState(false);
  const [adminError, setAdminError] = useState(null);

  useEffect(() => {
    const cRef = ref(db, `brain_health/communities/${communityCode}`);
    const unsubscribe = onValue(
      cRef,
      (snap) => setCommunity(snap.val()),
      (err) => setError(err.message)
    );
    return unsubscribe;
  }, [communityCode]);

  useEffect(() => {
    const accountsQuery = query(
      ref(db, 'brain_health/accounts'),
      orderByChild('community_code'),
      equalTo(communityCode)
    );
    const unsubscribe = onValue(
      accountsQuery,
      (snap) => setResidents(snap.val() || {}),
      (err) => setError(err.message)
    );
    return unsubscribe;
  }, [communityCode]);

  // The roster is split into active + archived. Each is sorted by
  // display name. We memoize so we don't re-sort on every render.
  const { activeResidents, archivedResidents } = useMemo(() => {
    if (!residents) return { activeResidents: [], archivedResidents: [] };
    const entries = Object.entries(residents);
    const active = entries.filter(([, r]) => r.status !== 'archived');
    const archived = entries.filter(([, r]) => r.status === 'archived');
    const byName = (a, b) =>
      (a[1].display_name || '').localeCompare(b[1].display_name || '');
    active.sort(byName);
    archived.sort(byName);
    return { activeResidents: active, archivedResidents: archived };
  }, [residents]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    let claimedCode = null;
    try {
      const code = await generateLoginCode(residentName);
      const accountId = push(ref(db, 'brain_health/accounts')).key;
      await claimLoginCode(code, accountId);
      claimedCode = code;

      const record = {
        display_name: residentName.trim(),
        login_code: code,
        community_code: communityCode,
        status: 'active',
        created_at: Date.now(),
      };
      if (residentNotes.trim()) {
        record.notes = residentNotes.trim();
      }

      await set(ref(db, `brain_health/accounts/${accountId}`), record);
      await set(ref(db, `brain_health/entitlements/${accountId}`), {
        source: 'community',
        status: 'active',
      });

      setJustCreated({ accountId, code, name: residentName.trim() });
      setResidentName('');
      setResidentNotes('');
      setShowForm(false);
    } catch (err) {
      if (claimedCode) {
        try { await releaseLoginCode(claimedCode); } catch {}
      }
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive(accountId, currentStatus) {
    setArchivingId(accountId);
    setError(null);
    try {
      const newStatus = currentStatus === 'archived' ? 'active' : 'archived';
      const updates = { status: newStatus };
      if (newStatus === 'archived') {
        updates.archived_at = Date.now();
      }
      await update(
        ref(db, `brain_health/accounts/${accountId}`),
        updates
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setArchivingId(null);
    }
  }

  // Open the pairing form for a specific resident. Closes any other
  // open form. Resets the input.
  function openPairingForm(accountId) {
    setPairingResidentId(accountId);
    setPairingCode('');
    setPairingError(null);
  }

  function closePairingForm() {
    setPairingResidentId(null);
    setPairingCode('');
    setPairingError(null);
  }

  // Submit the pairing code. Looks it up, validates expiration,
  // writes the UDID to the resident's account and the device_index,
  // cleans up the pairing record.
  async function handlePairDevice(accountId) {
    setPairingError(null);
    if (!pairingCode || pairingCode.trim().length < 4) {
      setPairingError('Please enter the code shown on the tablet.');
      return;
    }
    setPairingSubmitting(true);
    try {
      // Step 1: look up the pairing record
      const code = pairingCode.trim();
      const pairingRef = ref(db, `brain_health/device_pairing/${code}`);
      const pairingSnap = await get(pairingRef);
      const pairing = pairingSnap.val();

      if (!pairing) {
        throw new Error("That code wasn't found. Double-check what's shown on the tablet, or refresh the tablet to generate a new code.");
      }
      if (pairing.expires_at && pairing.expires_at < Date.now()) {
        throw new Error("That code has expired. Refresh the tablet to generate a new one.");
      }
      const udid = pairing.udid;
      if (!udid) {
        throw new Error("The pairing code is corrupted. Please refresh the tablet and try again.");
      }

      // Step 2: check the UDID isn't already paired to a DIFFERENT
      // resident. If it is, we'd silently steal the device, which
      // could be confusing. Force the admin to unpair first.
      const existingIndexSnap = await get(
        ref(db, `brain_health/device_index/${udid}`)
      );
      const existingIndex = existingIndexSnap.val();
      if (existingIndex && existingIndex.accountId && existingIndex.accountId !== accountId) {
        throw new Error("This tablet is already paired to another resident. Unpair it from that resident first.");
      }

      // Step 3: write the UDID to the resident's account
      await update(
        ref(db, `brain_health/accounts/${accountId}`),
        { device_udid: udid }
      );

      // Step 4: write the lookup index. This is what /welcome.html
      // queries to identify the device on its next refresh.
      await set(
        ref(db, `brain_health/device_index/${udid}`),
        { accountId, communityCode }
      );

      // Step 5: clean up the pairing record. Both directions —
      // by-code and by-udid. We do this last so a failure in
      // step 3 or 4 doesn't leave the pairing code unusable on
      // retry.
      await remove(ref(db, `brain_health/device_pairing/${code}`));
      await remove(ref(db, `brain_health/device_pairing_by_udid/${udid}`));

      closePairingForm();
    } catch (err) {
      setPairingError(err.message);
    } finally {
      setPairingSubmitting(false);
    }
  }

  // Unpair a device from a resident. Removes the device_udid from
  // the account record and deletes the device_index entry.
  async function handleUnpairDevice(accountId, udid) {
    if (!udid) return;
    setPairingError(null);
    setPairingSubmitting(true);
    try {
      await update(
        ref(db, `brain_health/accounts/${accountId}`),
        { device_udid: null }
      );
      await remove(ref(db, `brain_health/device_index/${udid}`));
      closePairingForm();
    } catch (err) {
      setPairingError(err.message);
    } finally {
      setPairingSubmitting(false);
    }
  }

  // ----- Admin management (super-admin only) -----
  //
  // The set of admins for a community lives in two mirrored places:
  //   - communities/{code}/admins/{emailKey}  (rules read this to authorize)
  //   - admin_index/{emailKey}/{code}          (useAdminAccess reads this)
  // Both are super-admin-write per the rules, so these handlers only
  // run for the super-admin (the panel itself is gated on isSuperAdmin).
  //
  // The primary admin (contact_email) is shown but cannot be removed
  // here — it's edited via the community record. Co-admins can be
  // added and removed freely.

  async function handleAddAdmin(e) {
    e.preventDefault();
    setAdminError(null);

    const email = newAdminEmail.trim().toLowerCase();
    if (!email) {
      setAdminError('Enter an email address to add.');
      return;
    }
    if (!isValidEmail(email)) {
      setAdminError('Please enter a valid email address.');
      return;
    }

    const key = emailToKey(email);
    const existingAdmins = community?.admins || {};
    const primaryEmail = (community?.contact_email || '').toLowerCase();
    if (email === primaryEmail || existingAdmins[key]) {
      setAdminError('That email is already an admin of this community.');
      return;
    }

    setAdminSubmitting(true);
    try {
      const entry = { email, added_at: Date.now() };
      const updates = {};
      updates[`brain_health/communities/${communityCode}/admins/${key}`] = entry;
      updates[`brain_health/admin_index/${key}/${communityCode}`] = entry;
      await update(ref(db), updates);
      setNewAdminEmail('');
    } catch (err) {
      setAdminError(err.message);
    } finally {
      setAdminSubmitting(false);
    }
  }

  async function handleRemoveAdmin(emailKey) {
    setAdminError(null);
    setAdminSubmitting(true);
    try {
      const updates = {};
      updates[`brain_health/communities/${communityCode}/admins/${emailKey}`] = null;
      updates[`brain_health/admin_index/${emailKey}/${communityCode}`] = null;
      await update(ref(db), updates);
    } catch (err) {
      setAdminError(err.message);
    } finally {
      setAdminSubmitting(false);
    }
  }

  if (!community && communityCode) {
    return (
      <div className="container admin-residents">
        <p className="admin-meta">Loading community…</p>
      </div>
    );
  }

  if (community === null) {
    return (
      <div className="container admin-residents">
        <h1 className="admin-title">Community not found</h1>
        <p>No community with code <code>{communityCode}</code>, or you don't have access to it.</p>
        {isSuperAdmin ? (
          <Link to="/admin/communities" className="btn btn-secondary">
            ← Back to communities
          </Link>
        ) : null}
      </div>
    );
  }

  // Where does the "back" link go? Super-admin: to /admin/communities.
  // Community admin with multiple: to /admin/picker. Otherwise: no back.
  const backLink = isSuperAdmin
    ? '/admin/communities'
    : adminCommunities.length > 1
      ? '/admin/picker'
      : null;

  return (
    <div className="container admin-residents">
      <div className="admin-bar fade-in-1">
        {backLink ? (
          <Link to={backLink} className="admin-bar-back">
            ← {isSuperAdmin ? 'All communities' : 'Switch community'}
          </Link>
        ) : <span />}
        <button type="button" className="admin-bar-logout" onClick={logout}>
          Signed in as {user.email} · Sign out
        </button>
      </div>

      <span className="eyebrow fade-in-1">Community Roster</span>
      <h1 className="admin-title fade-in-2">
        {community.name}
      </h1>
      <p className="admin-meta-row fade-in-3">
        <span><strong>Code:</strong> <code>{communityCode}</code></span>
        {community.contact_admin ? (
          <span> · <strong>Contact:</strong> {community.contact_admin}</span>
        ) : null}
        {community.contact_email ? (
          <span> · {community.contact_email}</span>
        ) : null}
      </p>

      {isSuperAdmin ? (
        <div className="admin-admins-panel fade-in-3">
          <button
            type="button"
            className="admin-admins-toggle"
            onClick={() => { setShowAdminPanel((v) => !v); setAdminError(null); }}
            aria-expanded={showAdminPanel}
          >
            {showAdminPanel ? '▾' : '▸'} Manage admins
            {' '}
            <span className="admin-admins-count">
              ({countAdmins(community)})
            </span>
          </button>

          {showAdminPanel ? (
            <div className="admin-admins-body">
              <p className="admin-admins-lede">
                Anyone listed here can sign in at{' '}
                <strong>brain.quiltt.com/admin/login</strong> and manage this
                community's residents. Only you (the super-administrator) can
                change this list.
              </p>

              <ul className="admin-admins-list">
                {buildAdminList(community).map((a) => (
                  <li key={a.key} className="admin-admins-item">
                    <span className="admin-admins-email">{a.email}</span>
                    {a.isPrimary ? (
                      <span className="admin-admins-badge">Primary contact</span>
                    ) : (
                      <button
                        type="button"
                        className="admin-table-action admin-table-action-quiet"
                        onClick={() => handleRemoveAdmin(a.key)}
                        disabled={adminSubmitting}
                      >
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              <form onSubmit={handleAddAdmin} className="admin-admins-add">
                <label htmlFor="new-admin-email" className="admin-form-label">
                  Add an admin
                </label>
                <div className="admin-admins-add-row">
                  <input
                    id="new-admin-email"
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    placeholder="colleague@community.com"
                    className="admin-form-input"
                    autoComplete="off"
                    disabled={adminSubmitting}
                  />
                  <button
                    type="submit"
                    className="btn"
                    disabled={adminSubmitting || !newAdminEmail.trim()}
                  >
                    {adminSubmitting ? 'Adding…' : 'Add admin'}
                  </button>
                </div>
                <span className="admin-form-hint">
                  They'll sign in with the same passwordless magic-link flow.
                  No password to share.
                </span>
              </form>

              {adminError ? (
                <p className="admin-error" role="alert">{adminError}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="admin-error" role="alert">{error}</p>
      ) : null}

      {lastAssessment ? (
        <div className="admin-card-callout">
          <span className="eyebrow">Assessment Saved</span>
          <h2 className="admin-card-callout-title">
            {lastAssessment.name}
          </h2>
          <p className="admin-card-callout-note">
            Score: <strong>{lastAssessment.score} / 26</strong> ·{' '}
            Level: <strong>{LEVELS[lastAssessment.level]?.label}</strong>
          </p>
        </div>
      ) : null}

      {justCreated ? (
        <div className="admin-card-callout">
          <span className="eyebrow">Resident Added</span>
          <h2 className="admin-card-callout-title">{justCreated.name}</h2>
          <div className="admin-card-callout-code">
            <span className="admin-card-callout-code-label">Sign-in code</span>
            <code className="admin-card-callout-code-value">
              {justCreated.code}
            </code>
            <button
              type="button"
              className="btn btn-secondary admin-card-callout-copy"
              onClick={() => navigator.clipboard.writeText(justCreated.code)}
            >
              Copy
            </button>
          </div>
          <p className="admin-card-callout-note">
            Share this code with {justCreated.name.split(' ')[0]} to sign in
            at <strong>brain.quiltt.com</strong>.
          </p>
          <button
            type="button"
            className="admin-card-callout-dismiss"
            onClick={() => setJustCreated(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="admin-section-header fade-in-4">
        <h2 className="admin-section-title">
          Residents ({activeResidents.length}
          {archivedResidents.length > 0 ? ` · ${archivedResidents.length} archived` : ''})
        </h2>
        {archivedResidents.length > 0 ? (
          <label className="admin-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span>Show archived</span>
          </label>
        ) : null}
      </div>

      {residents === null ? (
        <p className="admin-meta">Loading…</p>
      ) : activeResidents.length === 0 && !showArchived ? (
        <div className="admin-empty">
          <p>No active residents yet. Add the first one below.</p>
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Sign-in code</th>
              <th>Assessment</th>
              <th>Activity</th>
              <th>Last active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {activeResidents.map(([id, r]) => (
              <ResidentRow
                key={id}
                id={id}
                resident={r}
                onArchive={() => handleArchive(id, r.status)}
                archiving={archivingId === id}
                pairingOpen={pairingResidentId === id}
                onOpenPairing={() => openPairingForm(id)}
                onClosePairing={closePairingForm}
                pairingCode={pairingResidentId === id ? pairingCode : ''}
                onPairingCodeChange={setPairingCode}
                onPair={() => handlePairDevice(id)}
                onUnpair={() => handleUnpairDevice(id, r.device_udid)}
                pairingSubmitting={pairingSubmitting}
                pairingError={pairingResidentId === id ? pairingError : null}
                expanded={expandedResidentId === id}
                onToggleExpand={() =>
                  setExpandedResidentId((cur) => (cur === id ? null : id))
                }
              />
            ))}
            {showArchived && archivedResidents.length > 0 ? (
              <>
                <tr className="admin-table-divider">
                  <td colSpan="5">
                    <span className="admin-table-divider-label">Archived</span>
                  </td>
                </tr>
                {archivedResidents.map(([id, r]) => (
                  <ResidentRow
                    key={id}
                    id={id}
                    resident={r}
                    onArchive={() => handleArchive(id, r.status)}
                    archiving={archivingId === id}
                    pairingOpen={pairingResidentId === id}
                    onOpenPairing={() => openPairingForm(id)}
                    onClosePairing={closePairingForm}
                    pairingCode={pairingResidentId === id ? pairingCode : ''}
                    onPairingCodeChange={setPairingCode}
                    onPair={() => handlePairDevice(id)}
                    onUnpair={() => handleUnpairDevice(id, r.device_udid)}
                    pairingSubmitting={pairingSubmitting}
                    pairingError={pairingResidentId === id ? pairingError : null}
                    expanded={expandedResidentId === id}
                    onToggleExpand={() =>
                      setExpandedResidentId((cur) => (cur === id ? null : id))
                    }
                  />
                ))}
              </>
            ) : null}
          </tbody>
        </table>
      )}

      {!showForm ? (
        <button
          type="button"
          className="btn admin-add-button"
          onClick={() => setShowForm(true)}
        >
          + Add a resident
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="admin-form">
          <h2 className="admin-form-title">New resident</h2>

          <div className="admin-form-field">
            <label htmlFor="resident-name" className="admin-form-label">
              Display name *
            </label>
            <input
              id="resident-name"
              type="text"
              value={residentName}
              onChange={(e) => setResidentName(e.target.value)}
              placeholder="Margaret Stevens"
              required
              autoFocus
              className="admin-form-input"
            />
            <span className="admin-form-hint">
              First word becomes the login code prefix (e.g. MARGARET-742).
            </span>
          </div>

          <div className="admin-form-field">
            <label htmlFor="resident-notes" className="admin-form-label">
              Notes
            </label>
            <textarea
              id="resident-notes"
              value={residentNotes}
              onChange={(e) => setResidentNotes(e.target.value)}
              placeholder="Room number, family contact, anything else…"
              rows={2}
              className="admin-form-input"
            />
          </div>

          <div className="admin-form-actions">
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create resident'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setShowForm(false); setError(null); }}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// =================================================================
// ResidentRow — single row, with assessment status, archive action,
// and device pairing controls.
//
// The pairing UI has three states per row:
//   1. No device paired, form closed: shows "Pair device" action
//   2. No device paired, form open: shows code input + Save/Cancel
//   3. Device paired: shows "Device paired" + Unpair action
// =================================================================
function ResidentRow({
  id,
  resident: r,
  onArchive,
  archiving,
  pairingOpen,
  onOpenPairing,
  onClosePairing,
  pairingCode,
  onPairingCodeChange,
  onPair,
  onUnpair,
  pairingSubmitting,
  pairingError,
  expanded,
  onToggleExpand,
}) {
  const level = r.assessment?.current?.level;
  const score = r.assessment?.current?.score;
  const isArchived = r.status === 'archived';
  const isPaired = !!r.device_udid;

  // Usage stats from the resident's session history. The 30-day
  // figures reuse the dashboard's own helpers so the numbers match
  // what the resident sees; the per-game list is all-time.
  const engagement = useMemo(
    () => engagementSummary(r.sessions),
    [r.sessions]
  );
  const activity = useMemo(
    () => goalActivitySummary(r.sessions, { windowDays: 30 }),
    [r.sessions]
  );
  const games = useMemo(() => gameRollup(r.sessions), [r.sessions]);
  const hasActivity = activity.totalPlays > 0;
  const hasAnyHistory = games.length > 0;

  return (
    <>
      <tr className={isArchived ? 'admin-table-row-archived' : ''}>
        <td className="admin-table-name">{r.display_name}</td>
        <td><code className="admin-table-code">{r.login_code}</code></td>
        <td className="admin-table-status">
          {level
            ? <>{LEVELS[level]?.label} · <span className="admin-table-score">{score}/26</span></>
            : <span className="admin-table-untaken">Not assessed</span>
          }
        </td>
        <td className="admin-table-activity">
          {hasActivity || hasAnyHistory ? (
            <button
              type="button"
              className="admin-activity-toggle"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              title="Show usage detail"
            >
              <span className="admin-activity-caret" aria-hidden="true">
                {expanded ? '▾' : '▸'}
              </span>
              {hasActivity ? (
                <span className="admin-activity-summary">
                  {activity.totalPlays}{' '}
                  {activity.totalPlays === 1 ? 'play' : 'plays'}
                  {' · ~'}{activity.totalMinutes} min
                  <span className="admin-activity-window"> · 30d</span>
                </span>
              ) : (
                <span className="admin-activity-summary admin-activity-quiet">
                  None in 30 days
                </span>
              )}
              {engagement.streak > 0 ? (
                <span className="admin-activity-streak">
                  🔥 {engagement.streak}d
                </span>
              ) : null}
            </button>
          ) : (
            <span className="admin-table-untaken">No plays yet</span>
          )}
        </td>
        <td className="admin-table-meta">
          {r.last_active
            ? new Date(r.last_active).toLocaleDateString()
            : '—'}
        </td>
        <td className="admin-table-actions">
          {!isArchived ? (
            <>
              <Link
                to={`/admin/residents/${id}/assessment`}
                className="admin-table-action"
              >
                {level ? 'Re-take' : 'Take'} assessment
              </Link>
              {isPaired ? (
                <button
                  type="button"
                  className="admin-table-action admin-table-action-quiet"
                  onClick={onUnpair}
                  disabled={pairingSubmitting}
                  title={`Paired to device ${r.device_udid.slice(0, 8)}…`}
                >
                  Unpair device
                </button>
              ) : (
                <button
                  type="button"
                  className="admin-table-action admin-table-action-quiet"
                  onClick={onOpenPairing}
                  disabled={pairingSubmitting}
                >
                  Pair device
                </button>
              )}
              <button
                type="button"
                className="admin-table-action admin-table-action-quiet"
                onClick={onArchive}
                disabled={archiving}
              >
                {archiving ? 'Archiving…' : 'Archive'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="admin-table-action"
              onClick={onArchive}
              disabled={archiving}
            >
              {archiving ? 'Restoring…' : 'Restore'}
            </button>
          )}
        </td>
      </tr>
      {pairingOpen && !isArchived && !isPaired ? (
        <tr className="admin-table-pairing-row">
          <td colSpan="6">
            <div className="admin-pairing-form">
              <label htmlFor={`pair-${id}`} className="admin-pairing-label">
                Enter the pairing code shown on the tablet
              </label>
              <div className="admin-pairing-input-row">
                <input
                  id={`pair-${id}`}
                  type="text"
                  value={pairingCode}
                  onChange={(e) =>
                    onPairingCodeChange(e.target.value.toUpperCase().trim())
                  }
                  placeholder="e.g. 7K4MQR"
                  className="admin-pairing-input"
                  autoComplete="off"
                  autoFocus
                  disabled={pairingSubmitting}
                />
                <button
                  type="button"
                  className="btn"
                  onClick={onPair}
                  disabled={pairingSubmitting || !pairingCode}
                >
                  {pairingSubmitting ? 'Pairing…' : 'Pair'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClosePairing}
                  disabled={pairingSubmitting}
                >
                  Cancel
                </button>
              </div>
              {pairingError ? (
                <p className="admin-pairing-error" role="alert">
                  {pairingError}
                </p>
              ) : null}
              <p className="admin-pairing-hint">
                Turn on the tablet. A code will appear on the welcome
                screen. Enter that code here to associate the tablet
                with {r.display_name?.split(' ')[0] || 'this resident'}.
              </p>
            </div>
          </td>
        </tr>
      ) : null}
      {expanded ? (
        <tr className="admin-table-detail-row">
          <td colSpan="6">
            <div className="admin-usage">
              <div className="admin-usage-stats">
                <div className="admin-usage-stat">
                  <span className="admin-usage-stat-num">
                    {activity.totalPlays}
                  </span>
                  <span className="admin-usage-stat-label">
                    {activity.totalPlays === 1 ? 'session' : 'sessions'} · 30 days
                  </span>
                </div>
                <div className="admin-usage-stat">
                  <span className="admin-usage-stat-num">
                    ~{activity.totalMinutes}
                  </span>
                  <span className="admin-usage-stat-label">
                    estimated minutes · 30 days
                  </span>
                </div>
                <div className="admin-usage-stat">
                  <span className="admin-usage-stat-num">
                    {engagement.weekCount}
                  </span>
                  <span className="admin-usage-stat-label">
                    this week
                  </span>
                </div>
                <div className="admin-usage-stat">
                  <span className="admin-usage-stat-num">
                    {engagement.streak}
                  </span>
                  <span className="admin-usage-stat-label">
                    day {engagement.streak === 1 ? 'started' : 'streak'}
                  </span>
                </div>
              </div>

              {games.length > 0 ? (
                <div className="admin-usage-games">
                  <span className="admin-usage-games-title">
                    Most played <span className="admin-usage-games-note">(all time)</span>
                  </span>
                  <ul className="admin-usage-games-list">
                    {games.map((g) => (
                      <li key={g.gameId} className="admin-usage-game">
                        <span className="admin-usage-game-name">{g.name}</span>
                        <span className="admin-usage-game-plays">
                          {g.plays} {g.plays === 1 ? 'play' : 'plays'}
                        </span>
                        <span className="admin-usage-game-last">
                          last {new Date(g.lastPlayed).toLocaleDateString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="admin-usage-empty">
                  No game sessions recorded yet.
                </p>
              )}

              <p className="admin-usage-footnote">
                Minutes are estimated from each game's typical length —
                exact play time isn't tracked.
              </p>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// =================================================================
// Admin-list helpers (multi-admin model)
//
// Email → Firebase key: dots → commas. Must match emailToKey() in
// AdminCommunities.jsx and useAdminAccess.jsx and the rules'
// auth.token.email.replace('.', ',').
// =================================================================
function emailToKey(email) {
  return String(email || '').toLowerCase().replaceAll('.', ',');
}

function isValidEmail(email) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(email || '').trim());
}

// Number of distinct admins on a community: the primary contact_email
// plus any co-admins in the admins node (deduped against the primary).
function countAdmins(community) {
  return buildAdminList(community).length;
}

// Build the de-duplicated admin list for display. The primary
// contact_email is always first and flagged isPrimary (not removable
// here). Co-admins from communities/{code}/admins follow, sorted by
// email. Each entry carries its emailKey for removal.
function buildAdminList(community) {
  const out = [];
  const seen = new Set();

  const primary = (community?.contact_email || '').toLowerCase();
  if (primary) {
    const key = emailToKey(primary);
    seen.add(key);
    out.push({ key, email: primary, isPrimary: true });
  }

  const admins = community?.admins || {};
  Object.entries(admins)
    .map(([key, v]) => ({
      key,
      email: (v?.email || '').toLowerCase(),
    }))
    .filter((a) => a.email && !seen.has(a.key))
    .sort((a, b) => a.email.localeCompare(b.email))
    .forEach((a) => {
      seen.add(a.key);
      out.push({ ...a, isPrimary: false });
    });

  return out;
}
